/**
 * The sizes of a product with their EAN13 and M3 SKU codes (stock and central
 * flow), or a single "unique" size. Rows can be reordered by drag and drop.
 */
import { useState, type DragEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { Dialog } from '@/shared/ui/Dialog';
import plusIcon from '@/assets/images/plus.svg';
import {
  productQueryOptions,
  updateProduct,
  type Product,
  type ProductSize,
} from './api';
import { moveItem, splitSizes, type Edited } from './sizes-form';

export interface AdditionalDataDialogProps {
  item: Product;
  onClose: (result: 'Confirm' | 'Cancel') => void;
}

type CodeKey =
  'ean13Stock' | 'ean13CentralFlow' | 'm3SkuStock' | 'm3SkuCentralFlow';

const CODES: { key: CodeKey; width: string; testId: string }[] = [
  { key: 'ean13Stock', width: '20%', testId: 'ean13-stock' },
  { key: 'ean13CentralFlow', width: '20%', testId: 'ean13-central-flow' },
  { key: 'm3SkuStock', width: '20%', testId: 'm3-sku-stock' },
  { key: 'm3SkuCentralFlow', width: '20%', testId: 'm3-sku-central-flow' },
];

export function AdditionalDataDialog({
  item,
  onClose,
}: Readonly<AdditionalDataDialogProps>) {
  const product = useQuery(productQueryOptions(item.id));
  const [edits, setEdits] = useState<Edited | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [dragged, setDragged] = useState<number | null>(null);
  const state = edits ?? (product.data ? splitSizes(product.data) : null);
  const loadFailed = product.isError;

  const update = (patch: Partial<Edited>) => {
    if (state) setEdits({ ...state, ...patch });
  };
  const hasUniqueSize = !!state?.product.hasUniqueSize;

  const save = useMutation({
    mutationFn: async () => {
      if (!state) return;
      await updateProduct({
        ...state.product,
        sizes: hasUniqueSize ? [state.uniqueSize] : state.sizes,
      });
    },
    onSuccess: () => onClose('Confirm'),
    onError: () => setErrorMessage('Failed to update product additional data'),
  });

  const setSize = (index: number, patch: Partial<ProductSize>) =>
    update({
      sizes: state!.sizes.map((size, i) =>
        i === index ? { ...size, ...patch } : size,
      ),
    });

  const onDrop = (event: DragEvent, index: number) => {
    event.preventDefault();
    if (dragged === null || dragged === index || !state) return;
    update({ sizes: moveItem(state.sizes, dragged, index) });
    setDragged(null);
  };

  const codeInput = (
    value: string | null | undefined,
    label: string,
    readOnly: boolean,
    onChange: (value: string) => void,
    testId: string,
    style?: React.CSSProperties,
  ) => (
    <input
      className="field"
      aria-label={label}
      data-testid={testId}
      value={value ?? ''}
      readOnly={readOnly}
      style={style}
      onChange={(event) => onChange(event.target.value)}
    />
  );

  return (
    <Dialog
      title="Additional Data"
      onClose={() => onClose('Cancel')}
      testId="product-additional-data-dialog"
    >
      <div className="mb-4">
        <div className="table-container">
          <div
            className="items-container"
            style={{ maxHeight: 420, overflow: 'auto' }}
            role="region"
            aria-label="Sizes"
          >
            <table className="table items">
              <thead>
                <tr>
                  <th style={{ width: '15%' }}>Unique Size</th>
                  <th style={{ width: '20%' }}>EAN13 Stock</th>
                  <th style={{ width: '20%' }}>EAN13 Central Flow</th>
                  <th style={{ width: '20%' }}>M3SKU Stock</th>
                  <th style={{ width: '20%' }}>M3SKU Central Flow</th>
                  <th style={{ width: '5%' }} className="edit-action" />
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td
                    style={{ width: '15%', paddingLeft: 30, paddingTop: 22 }}
                    title="U"
                  >
                    <div className="styled-input-square styled-input-single">
                      <input
                        type="checkbox"
                        id="has-unique-size"
                        data-testid="product-additional-data-form-has-unique-size"
                        name="hasUniqueSize"
                        checked={hasUniqueSize}
                        disabled={!state}
                        onChange={(event) =>
                          update({
                            product: {
                              ...state!.product,
                              hasUniqueSize: event.target.checked,
                            },
                          })
                        }
                      />
                      <label htmlFor="has-unique-size">Unique</label>
                    </div>
                  </td>
                  {CODES.map(({ key, width, testId }) => (
                    <td
                      key={key}
                      style={{ width }}
                      title={state?.uniqueSize[key] ?? ''}
                    >
                      {codeInput(
                        state?.uniqueSize[key],
                        `Unique ${key}`,
                        !hasUniqueSize,
                        (value) =>
                          update({
                            uniqueSize: { ...state!.uniqueSize, [key]: value },
                          }),
                        `product-additional-data-form-unique-${testId}`,
                      )}
                    </td>
                  ))}
                  <td style={{ width: '5%' }} className="edit-action" />
                </tr>
                {state?.sizes.map((size, index) => (
                  <tr
                    key={index}
                    hidden={hasUniqueSize}
                    className="cursor-move"
                    draggable
                    onDragStart={() => setDragged(index)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => onDrop(event, index)}
                    onDragEnd={() => setDragged(null)}
                    data-testid="product-additional-data-row"
                  >
                    <td style={{ width: '15%' }} title={size.size ?? ''}>
                      {codeInput(
                        size.size,
                        `Size ${index + 1}`,
                        hasUniqueSize,
                        (value) => setSize(index, { size: value }),
                        'product-additional-data-row-size',
                        { width: 100 },
                      )}
                    </td>
                    {CODES.map(({ key, width, testId }) => (
                      <td key={key} style={{ width }} title={size[key] ?? ''}>
                        {codeInput(
                          size[key],
                          `Size ${index + 1} ${key}`,
                          hasUniqueSize,
                          (value) => setSize(index, { [key]: value }),
                          `product-additional-data-row-${testId}`,
                        )}
                      </td>
                    ))}
                    <td style={{ width: '5%' }} className="edit-action">
                      <button
                        type="button"
                        aria-label={`Remove size ${index + 1}`}
                        data-testid="product-additional-data-row-delete-button"
                        style={{
                          display: 'inline-block',
                          marginTop: 8,
                          cursor: 'pointer',
                        }}
                        onClick={() =>
                          update({
                            sizes: state.sizes.filter((_, i) => i !== index),
                          })
                        }
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mb-2 mt-1 flex justify-center">
            <button
              type="button"
              className={`add-btn flex items-center ${hasUniqueSize ? 'disabled' : ''}`}
              title={
                hasUniqueSize ? 'Untick unique checkbox to add more lines' : ''
              }
              disabled={!state}
              data-testid="product-additional-data-form-add-line-button"
              onClick={() => {
                if (!hasUniqueSize)
                  update({ sizes: [...state!.sizes, { size: '' }] });
              }}
            >
              <img src={plusIcon} alt="" />
              <span className="pl-2">Add a line</span>
            </button>
          </div>

          {(errorMessage || loadFailed) && (
            <div
              className="alert mb-2"
              role="alert"
              data-testid="product-additional-data-form-error"
            >
              <span className="error-msg">
                {errorMessage ?? 'Failed to update product additional data'}
              </span>
            </div>
          )}
          <div className="mb-4 mt-2 flex justify-center">
            <button
              type="button"
              className="button button-form"
              disabled={!state || save.isPending}
              data-testid="product-additional-data-form-save-button"
              onClick={() => {
                setErrorMessage(null);
                save.mutate();
              }}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
